/**
 * Partner Benchmark maths, ported 1:1 from benchmark-mock (data/fetch.py totals, data/aggregate.py,
 * render/app.js helpers and the heatmap/ranking views).
 *
 * Pure functions (no React, no DOM) over the season-total rows of GET dashboard/benchmark/:
 *   { partner, channel, meValue, impressions, exposures, duration }
 */

export const TIER_CUT = 20; // top 20 partners by season ME value = Tier 1
export const MIN_N = 6; // fewer samples => benchmark null
export const DASH = '—';
export const METRICS = { meValue: 'ME Value', impressions: 'Raw Impressions', exposures: 'Exposures', duration: 'Duration' };
export const SCOPES = { all: 'All', tier1: 'Tier 1', tier2: 'Tier 2' };
export const STATS = { median: 'Median', mean: 'Mean' };
export const CHANNEL_ORDER = ['TV', 'SOCIAL', 'IN-VENUE', 'DIGITAL', 'OOH'];

const METRIC_KEYS = Object.keys(METRICS);

// CHANNEL_ORDER entries present in rows, then any other channels alphabetically.
function channelList(rows) {
    const present = new Set(rows.map(r => r.channel));
    const extra = [...present].filter(c => !CHANNEL_ORDER.includes(c)).sort();
    return CHANNEL_ORDER.filter(c => present.has(c)).concat(extra);
}

// fetch.py totals (first-seen order, stable sort by ME value desc) + aggregate.assign_tiers.
function rankPartners(rows) {
    const totals = new Map();
    rows.forEach(r => {
        if (!totals.has(r.partner)) totals.set(r.partner, { meValue: 0, impressions: 0, exposures: 0, duration: 0 });
        const total = totals.get(r.partner);
        METRIC_KEYS.forEach(m => { total[m] += r[m]; });
    });
    return [...totals].map(([name, total]) => ({ name, total }))
        .sort((a, b) => b.total.meValue - a.total.meValue)
        .map((partner, index) => ({ ...partner, tier: index < TIER_CUT ? 1 : 2 }));
}

// aggregate._quantile: nearest observed value.
const quantile = (sorted, fraction) => sorted[Math.floor((sorted.length - 1) * fraction + 0.5)];

function stats(values) {
    const n = values.length;
    if (n < MIN_N) return null;
    const sorted = values.slice().sort((a, b) => a - b);
    const mid = Math.floor(n / 2);
    return {
        n,
        median: n % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2,
        mean: values.reduce((sum, v) => sum + v, 0) / n,
        p25: quantile(sorted, 0.25),
        p75: quantile(sorted, 0.75),
    };
}

// aggregate.benchmarks: bench[channel][scope][metric].
function benchmarks(rows, partners, channels) {
    const tiers = new Map(partners.map(p => [p.name, p.tier]));
    const grouped = {};
    channels.forEach(c => { grouped[c] = { all: [], tier1: [], tier2: [] }; });
    rows.forEach(r => {
        grouped[r.channel].all.push(r);
        grouped[r.channel]['tier' + tiers.get(r.partner)].push(r);
    });
    const bench = {};
    channels.forEach(c => {
        bench[c] = {};
        Object.keys(SCOPES).forEach(scope => {
            bench[c][scope] = {};
            METRIC_KEYS.forEach(m => { bench[c][scope][m] = stats(grouped[c][scope].map(r => r[m])); });
        });
    });
    return bench;
}

export function buildModel(rows) {
    const channels = channelList(rows);
    const partners = rankPartners(rows);
    const cells = new Map();
    rows.forEach(r => {
        if (!cells.has(r.partner)) cells.set(r.partner, new Map());
        cells.get(r.partner).set(r.channel, r);
    });
    return { channels, partners, rows, bench: benchmarks(rows, partners, channels), cells };
}

export const inScope = (partner, scope) => scope === 'all' || 'tier' + partner.tier === scope;
export const centre = (bench, stat) => (bench ? bench[stat] : null);
export const idx = (v, centreValue) =>
    (Number.isFinite(v) && Number.isFinite(centreValue) && centreValue > 0 ? v / centreValue : null);
export const idxText = r => (Number.isFinite(r) ? 'x' + r.toFixed(1) : DASH);

const numberFormat = new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 });

export function compact(v) {
    const magnitude = Math.abs(v);
    const scale = magnitude >= 1e9 ? [1e9, 'B'] : magnitude >= 1e6 ? [1e6, 'M'] : magnitude >= 1e3 ? [1e3, 'K'] : [1, ''];
    return numberFormat.format(v / scale[0]) + scale[1];
}

export function fmt(metric, v) {
    if (v === null || v === undefined || !Number.isFinite(v)) return DASH;
    if (metric === 'duration') return v >= 3600 ? numberFormat.format(v / 3600) + ' h' : numberFormat.format(v) + ' s';
    return (metric === 'meValue' ? '$' : '') + compact(v);
}

// Partner's index against the channel benchmark; null without delivery or benchmark.
export function cellRatio(model, partnerName, channel, { scope, metric, stat }) {
    const row = model.cells.has(partnerName) ? model.cells.get(partnerName).get(channel) : undefined;
    const bench = model.bench[channel] ? model.bench[channel][scope][metric] : null;
    return row && bench ? idx(row[metric], centre(bench, stat)) : null;
}

// Diverging tint on a log2 scale, capped at x0.25 / x4.
export function tint(ratio) {
    if (ratio === null) return null;
    const position = Math.log2(Math.min(4, Math.max(0.25, ratio))) / 2;
    return { side: position < 0 ? 'below' : 'above', strength: Math.abs(position) * 0.32 };
}

const missing = v => v === null || v === undefined;

export function heatmapRows(model, { scope, metric, stat, partners }, sort) {
    const rows = model.partners.filter(p => inScope(p, scope)).map(partner => {
        const ratios = {};
        model.channels.forEach(c => { ratios[c] = cellRatio(model, partner.name, c, { scope, metric, stat }); });
        return { partner, ratios };
    });
    const value = row => (sort.column === 'Total' ? row.partner.total[metric] : row.ratios[sort.column]);
    const selected = new Map(partners.map((name, index) => [name, index]));
    const pin = row => (selected.has(row.partner.name) ? selected.get(row.partner.name) : Infinity);
    return rows.sort((a, b) => {
        if (selected.has(a.partner.name) || selected.has(b.partner.name)) return pin(a) - pin(b);
        const av = value(a), bv = value(b);
        if (missing(av)) return missing(bv) ? 0 : 1;
        if (missing(bv)) return -1;
        return (sort.descending ? bv - av : av - bv) || a.partner.name.localeCompare(b.partner.name);
    });
}

export function rankingRows(model, channel, scope, metric) {
    const names = new Set(model.partners.filter(p => inScope(p, scope)).map(p => p.name));
    return model.rows.filter(r => r.channel === channel && names.has(r.partner))
        .sort((a, b) => b[metric] - a[metric] || a.partner.localeCompare(b.partner));
}

export const logPosition = (value, max) => (max > 0 ? Math.log10(value + 1) / Math.log10(max + 1) * 100 : 0);
